import { useCallback, useEffect, useRef, useState } from "react"

// Finds Brand check flags in the words on the page, so a coach (or KF admin)
// reads each one in context. The page's HTML never changes: the words are
// painted with the CSS Custom Highlight API, so "Edit it yourself" cannot
// save a highlight into the chapter.
//
// The match ignores curly quotes, spacing, and case, the same as
// BrandChecker#findable?, so every quote the check kept is found again.

const QUOTES = { "“": "\"", "”": "\"", "‘": "'", "’": "'" }
const BLOCKS = "p, li, h1, h2, h3, h4, h5, h6, blockquote, cite, aside, section, div"
const PAINTS = ["kf-flag-note", "kf-flag-serious", "kf-flag-active"]

// Each character as the Brand check compares it, with the index it came from.
function normalize(text) {
  const chars = []
  const from = []
  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (/\s/.test(char)) {
      if (chars.length > 0 && chars[chars.length - 1] !== " ") {
        chars.push(" ")
        from.push(i)
      }
    } else {
      chars.push((QUOTES[char] || char).toLowerCase()[0])
      from.push(i)
    }
  }
  return { text: chars.join(""), from }
}

// Where the quote is in the text: [start, end] in the text's own indexes.
export function findInText(text, quote) {
  const hay = normalize(text || "")
  const needle = normalize(quote || "").text.trim()
  if (!needle) return null
  const at = hay.text.indexOf(needle)
  if (at < 0) return null
  return [hay.from[at], hay.from[at + needle.length - 1] + 1]
}

// A DOM Range over the quote under `root`. Text in two blocks (two
// paragraphs) gets a line break between it, as the Brand check reads it.
export function findInElement(root, quote) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const pieces = []
  let text = ""
  let block = null
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const nodeBlock = node.parentElement?.closest(BLOCKS)
    if (block && nodeBlock !== block) text += "\n"
    block = nodeBlock
    pieces.push({ node, start: text.length })
    text += node.data
  }
  const found = findInText(text, quote)
  if (!found) return null

  const at = (index) => {
    const piece = pieces.findLast((p) => p.start <= index)
    return [piece.node, index - piece.start]
  }
  const range = document.createRange()
  range.setStart(...at(found[0]))
  const [endNode, endOffset] = at(found[1] - 1)
  range.setEnd(endNode, endOffset + 1)
  return range
}

const canPaint = () => typeof CSS !== "undefined" && "highlights" in CSS

function sectionOf(range) {
  return range.startContainer.parentElement?.closest("section")?.querySelector("h2")?.textContent?.trim() || null
}

// Highlights `flags` in the element at `ref`, and finds them again whenever
// its words change. Returns:
//   places — null until the first look, then { [flagId]: { section } } for each flag found
//   show   — scrolls to a flag's words and flashes them
//   picked — { id } of the flag whose words were last clicked (when `pickable`)
// `watch` looks again when the element itself may have come or gone.
export function useFlagHighlights(ref, flags, { pickable = false, watch = null } = {}) {
  const [places, setPlaces] = useState(null)
  const [picked, setPicked] = useState(null)
  const ranges = useRef({})
  const flash = useRef(null)
  const flagKey = flags.map((flag) => `${flag.id}:${flag.severity}:${flag.quote}`).join("|")

  useEffect(() => {
    const root = ref.current
    if (!root) return undefined
    let frame = null

    const find = () => {
      frame = null
      const next = {}
      const found = {}
      flags.forEach((flag) => {
        const range = findInElement(root, flag.quote)
        if (!range) return
        next[flag.id] = range
        found[flag.id] = { section: sectionOf(range) }
      })
      ranges.current = next
      if (canPaint()) {
        const of = (severity) => flags.filter((flag) => flag.severity === severity && next[flag.id]).map((flag) => next[flag.id])
        CSS.highlights.set("kf-flag-note", new Highlight(...of("note")))
        CSS.highlights.set("kf-flag-serious", new Highlight(...of("serious")))
      }
      setPlaces((prev) => (JSON.stringify(prev) === JSON.stringify(found) ? prev : found))
    }

    find()
    const observer = new MutationObserver(() => {
      if (!frame) frame = requestAnimationFrame(find)
    })
    observer.observe(root, { childList: true, subtree: true, characterData: true })
    return () => {
      observer.disconnect()
      if (frame) cancelAnimationFrame(frame)
      if (canPaint()) PAINTS.forEach((name) => CSS.highlights.delete(name))
    }
  }, [ref, flagKey, watch])

  // A click on flagged words (read mode only) picks that flag.
  useEffect(() => {
    const root = ref.current
    if (!root || !pickable) return undefined
    const onClick = (event) => {
      if (!window.getSelection()?.isCollapsed) return
      const caret = document.caretPositionFromPoint?.(event.clientX, event.clientY)
      const point = caret ? [caret.offsetNode, caret.offset] : (() => {
        const range = document.caretRangeFromPoint?.(event.clientX, event.clientY)
        return range ? [range.startContainer, range.startOffset] : null
      })()
      if (!point) return
      const id = Object.keys(ranges.current).find((key) => ranges.current[key].isPointInRange(...point))
      if (id) setPicked({ id: Number(id) })
    }
    root.addEventListener("click", onClick)
    return () => root.removeEventListener("click", onClick)
  }, [ref, pickable, watch])

  const show = useCallback((id) => {
    const range = ranges.current[id]
    if (!range) return
    range.startContainer.parentElement?.scrollIntoView({ behavior: "smooth", block: "center" })
    if (canPaint()) {
      const active = new Highlight(range)
      active.priority = 1
      CSS.highlights.set("kf-flag-active", active)
      clearTimeout(flash.current)
      flash.current = setTimeout(() => CSS.highlights.delete("kf-flag-active"), 2500)
    } else {
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
    }
  }, [])

  return { places, show, picked }
}

// The same for words in a <textarea> (About the Author): `show` selects them.
export function useTextareaFlags(ref, text, flags) {
  const found = {}
  const spans = {}
  flags.forEach((flag) => {
    const span = findInText(text, flag.quote)
    if (span) {
      spans[flag.id] = span
      found[flag.id] = { section: null }
    }
  })
  const spanKey = JSON.stringify(spans)

  const show = useCallback((id) => {
    const field = ref.current
    const span = JSON.parse(spanKey)[id]
    if (!field || !span) return
    field.scrollIntoView({ behavior: "smooth", block: "center" })
    field.focus({ preventScroll: true })
    field.setSelectionRange(span[0], span[1])
  }, [ref, spanKey])

  return { places: found, show, picked: null }
}
